import { useEffect } from "react";

import { useAiAnalyze, type AiAnalyzeResponse } from "@/api/ai";
import { ApiError } from "@/api/client";
import { AiNote } from "@/components/AiNote";
import type { Period } from "@/components/PeriodTabs";
import { ModalShell } from "@/components/ui";
import { t } from "@/i18n";

interface AiAnalyzeModalProps {
  open: boolean;
  title: string;
  /** null ise modal kapalidir (open=false ile ayni etki, kolaylik icin). */
  kpiId: string | null;
  days: Period;
  modalRows?: unknown[];
  /** Modal her acildiginda/kpiId-prompt degistiginde gonderilecek soru; degismezse yeniden istek atilmaz. */
  prompt?: string;
  onClose: () => void;
}

function hataMesaji(error: unknown): string {
  if (error instanceof ApiError) return typeof error.detail === "string" ? error.detail : error.message;
  return t("common.error");
}

/**
 * Kart AI analizi + gidisat hero takip sorulari icin PAYLASILAN modal —
 * her ikisi de `POST /api/v2/ai/analyze/free-analyze` (`{kpi_id, days,
 * modal_rows, user_prompt}`) ucuna gider (bkz. app/core/api/ai.py: `kpi_id`
 * verilince backend use_case'i HER ZAMAN free-analyze'e sabitler). Kabuk
 * paylasilan `ModalShell` — Escape/backdrop/body-scroll-kilit orada yonetilir.
 */
export function AiAnalyzeModal({ open, title, kpiId, days, modalRows, prompt, onClose }: AiAnalyzeModalProps) {
  const mutation = useAiAnalyze("free-analyze");

  useEffect(() => {
    if (!open || !kpiId) return;
    mutation.mutate({ kpi_id: kpiId, days, modal_rows: modalRows, user_prompt: prompt });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalniz modal acilis/soru degisince yeniden istek atilir; mutation kimligi her render'da degisir.
  }, [open, kpiId, days, prompt]);

  if (!open || !kpiId) return null;

  const response: AiAnalyzeResponse["response"] | undefined = mutation.data?.response;

  return (
    <ModalShell open title={title} onClose={onClose} size="md">
      {mutation.isPending && <p className="text-sm text-muted">{t("ozet.ai.analiz_yukleniyor")}</p>}
      {mutation.isError && (
        <p role="alert" className="text-sm text-red">
          {hataMesaji(mutation.error)}
        </p>
      )}
      {!mutation.isPending && !mutation.isError && response && (
        <AiNote
          markdown={response.answer_markdown ?? ""}
          followUps={response.follow_up_prompts ?? []}
          onFollowUp={(q) => mutation.mutate({ kpi_id: kpiId, days, modal_rows: modalRows, user_prompt: q })}
        />
      )}
    </ModalShell>
  );
}
